import React, { useCallback } from 'react';
import { ItineraryTypes } from '../types';
import { ItemMedia } from './ItemMedia';
import { StayBadge } from 'ui/StayBadge/StayBadge';
import Star from 'ui/Icons/star.component.svg';
import { SvgIcon } from 'ui/SvgIcon';
import { VerticalSpace } from 'ui/VerticalSpace';
import { ItemNotesAndAttachments } from './ItemNotesAndAttachments';
import { SeeMoreCollapsible } from 'ui/SeeMoreCollapsible';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import { ReviewSummary } from 'containers/ReviewPage/ReviewSummary';
import { useReviewPageQueries } from 'containers/ReviewPage/hooks/useReviewPageQueries';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';

export interface iHotelMarkerProps {
  item: ItineraryTypes['ItineraryItem'];
  hotel?: ItineraryTypes['ItineraryHotelWithoutUploads'];
  totalAccomCount: number;
}

export const HotelMarker = (props: iHotelMarkerProps) => {
  const { item, hotel } = props;
  const dynamicParameters = ParameterService.getParameters();
  const { getHotelReviews } = useReviewPageQueries(makeBookingManagerApi(), hotel?.uuid, dynamicParameters.ENABLE_HOTEL_REVIEWS_TEMP);
  const hotelGeneralScore = getHotelReviews.data?.data?.generalScore;
  const description = item.ancillary_description || hotel?.description;

  const UpperComponent = hotelGeneralScore ?
    <span className="flex items-center gap-2">
      <SvgIcon IconComponent={Star} className="fill-brown-prime stroke-none" />
      <span className="font-hurmegeometric-sans text-12px text-brown-prime uppercase">
        {hotel?.starRating} Star
      </span>
    </span> : null;

  const MiddleComponent = hotelGeneralScore ?
    <h2 className="m-0 p-0 font-noe-display text-[20px] text-brown-prime">{hotel?.name}</h2> :
    <span className="flex items-center gap-2">
      <SvgIcon IconComponent={Star} className="fill-brown-prime stroke-none" />
      <span className="font-hurmegeometric-sans text-12px text-brown-prime uppercase">
        {hotel?.starRating} Star
      </span>
    </span>;

  const LowerComponent = hotelGeneralScore ?
    <ReviewSummary hotelUuid={hotel?.uuid} /> :
    <h2 className="m-0 p-0 font-noe-display text-[20px] text-brown-prime">{hotel?.name}</h2>;

  return (
    <div>
      <h2 className="m-0 font-hurmegeometric-sans uppercase text-flint text-[14px] mb-5px">Stay</h2>
      {UpperComponent}
      {/* star rating and stay badge */}
      <div className="flex justify-between items-center mb-5px">
        {MiddleComponent}
        <StayBadge startDate={item.date!} endDate={item.endDate!} />
      </div>

      <div className="flex justify-between items-center mb-5px">
        {LowerComponent}
        <span className="font-hurmegeometric-sans text-13px text-flint uppercase">{props.totalAccomCount} rooms</span>
      </div>

      {/* then media */}
      <ItemMedia
        uploads={item.uploads as ItineraryTypes['ItineraryImageUploadOrThumbnail'][]}
        titleForModal={hotel?.name || ''}
      />

      <VerticalSpace height="10px" />

      <SeeMoreCollapsible cutoffHeightPx={100}>
        <div className="flex flex-col gap-4">
          {description && (
            <HtmlStringRenderer
              content={description}
              className="font-hurmegeometric-sans text-14px text-flint leading-22px"
              fallbackComponent="span"
            />
          )}
          {item.hotel_overview && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                Overview:
              </span>
              <HtmlStringRenderer
                content={item.hotel_overview}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
          {item.hotel_highlights && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                highlights:
              </span>
              <HtmlStringRenderer
                content={item.hotel_highlights}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
          {item.hotel_amenities && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                amenities:
              </span>
              <HtmlStringRenderer
                content={item.hotel_amenities}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
          {item.hotel_additionalInfo && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                things to be aware of:
              </span>
              <HtmlStringRenderer
                content={item.hotel_additionalInfo}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}

          {item.hotel_policiesAndRestrictions && (
            <div>
              <span className="block uppercase font-hurmegeometric-sans text-14px text-flint leading-22px">
                policies and restrictions:
              </span>
              <HtmlStringRenderer
                content={item.hotel_policiesAndRestrictions}
                className="block font-hurmegeometric-sans text-14px text-flint leading-22px"
                fallbackComponent="span"
              />
            </div>
          )}
        </div>
      </SeeMoreCollapsible>

      <VerticalSpace height="10px" />

      {(item.notes.length > 0 || item.uploads.length > 0) && (
        <>
          <VerticalSpace height="10px" />
          <ItemNotesAndAttachments notes={item.notes} uploads={item.uploads} />
          <VerticalSpace height="40px" />
        </>
      )}

      {item.notes.length === 0 && item.uploads.length === 0 && (
        <>
          <VerticalSpace height="40px" />
        </>
      )}
    </div>
  );
};
